import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

// Attach bearer token from localStorage on every request
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("ft_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default client;

// ---------- Auth ----------
export const apiLogin = (email, password, role) =>
  client.post("/auth/login", { email, password, role }).then((r) => r.data);

export const apiSignup = (data) =>
  client.post("/auth/signup", data).then((r) => r.data);

export const apiGoogleSession = (session_id) =>
  client.post("/auth/google/session", { session_id }).then((r) => r.data);

export const apiMe = () => client.get("/auth/me").then((r) => r.data);

export const apiLogout = () => client.post("/auth/logout").then((r) => r.data);

// ---------- Flights ----------
export const apiFlightsStatus = () =>
  client.get("/flights/status").then((r) => r.data);

export const apiSearchFlights = (payload) =>
  client.post("/flights/search", payload).then((r) => r.data);

export const apiReviewFlight = (priceId) =>
  client.post("/flights/review", Array.isArray(priceId) ? { priceIds: priceId } : { priceId }).then((r) => r.data);

export const apiFlightSeatmap = (bookingId) =>
  client.post("/flights/seatmap", { bookingId }).then((r) => r.data);

export const apiBookFlightLive = (payload) =>
  client.post("/flights/book", payload).then((r) => r.data);

export const apiHotelLandmarks = (lat, lng) =>
  client.get(`/hotels/landmarks?lat=${lat}&lng=${lng}`).then((r) => r.data);

export const apiBookHotelLive = (payload) =>
  client.post("/hotels/book-live", payload).then((r) => r.data);

// ---------- Bookings ----------
export const apiCreateBooking = (payload) =>
  client.post("/bookings", payload).then((r) => r.data);

export const apiListBookings = () =>
  client.get("/bookings").then((r) => r.data);

// ---------- Enquiries / Reviews ----------
export const apiCreateEnquiry = (payload) =>
  client.post("/enquiries", payload).then((r) => r.data);

export const apiListEnquiries = () =>
  client.get("/enquiries").then((r) => r.data);

export const apiEnquiryStatus = (id, status) =>
  client.post(`/admin/enquiries/${id}/status?status=${status}`).then((r) => r.data);

export const apiListReviews = () =>
  client.get("/reviews").then((r) => r.data);

export const apiAddReview = (payload) =>
  client.post("/reviews", payload).then((r) => r.data);

// ---------- Wallet ----------
export const apiWalletTxns = () =>
  client.get("/wallet/transactions").then((r) => r.data);

// ---------- Admin ----------
export const apiAdminStats = () =>
  client.get("/admin/stats").then((r) => r.data);

export const apiAdminAgents = () =>
  client.get("/admin/agents").then((r) => r.data);

export const apiApproveAgent = (id) =>
  client.post(`/admin/agents/${id}/approve`).then((r) => r.data);
export const apiRejectAgent = (id, reason) =>
  client.post(`/admin/agents/${id}/reject`, { reason }).then((r) => r.data);
export const apiKycUpload = (file, docType) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post(`/kyc/upload?doc_type=${encodeURIComponent(docType)}`, fd,
    { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const kycFileUrl = (fileId) =>
  `${API}/kyc/file/${fileId}?auth=${encodeURIComponent(localStorage.getItem("ft_token") || "")}`;

export const apiSuspendAgent = (id) =>
  client.post(`/admin/agents/${id}/suspend`).then((r) => r.data);

export const apiAddWallet = (id, amount, note) =>
  client.post(`/admin/agents/${id}/wallet`, { amount, note }).then((r) => r.data);

// ---------- Hotels (TripJack v3 live) ----------
export const apiHotelCities = (q) =>
  client.get("/hotels/cities", { params: { q } }).then((r) => r.data);

export const apiHotelSuggest = (q) =>
  client.get("/hotels/suggest", { params: { q } }).then((r) => r.data);

export const apiSearchAirports = (q, limit = 15) =>
  client.get("/airports/search", { params: { q, limit } }).then((r) => r.data);

export const apiSearchAirlines = (q, limit = 20) =>
  client.get("/airlines/search", { params: { q, limit } }).then((r) => r.data);

export const apiHotelsStatus = () =>
  client.get("/hotels/status").then((r) => r.data);

export const apiSearchHotels = (payload) =>
  client.post("/hotels/search", payload).then((r) => r.data);

export const apiHotelPricing = (payload) =>
  client.post("/hotels/pricing", payload).then((r) => r.data);

export const apiHotelReview = (payload) =>
  client.post("/hotels/review", payload).then((r) => r.data);

export const apiHotelBook = (payload) =>
  client.post("/hotels/book", payload).then((r) => r.data);

// ---------- B2B modules ----------
export const apiGetMarkup = () => client.get("/markup").then((r) => r.data);
export const apiSetMarkup = (p) => client.post("/markup", p).then((r) => r.data);
export const apiSaveQuote = (p) => client.post("/quotations", p).then((r) => r.data);
export const apiListQuotes = () => client.get("/quotations").then((r) => r.data);
export const apiDeleteQuote = (id) => client.delete(`/quotations/${id}`).then((r) => r.data);
export const apiCreateDeposit = (p) => client.post("/wallet/deposit-request", p).then((r) => r.data);
export const apiWalletTopupOrder = (amount) => client.post("/wallet/topup/order", { amount }).then((r) => r.data);
export const apiWalletTopupVerify = (payload) => client.post("/wallet/topup/verify", payload).then((r) => r.data);
export const apiAdminWalletTopups = () => client.get("/admin/wallet-topups").then((r) => r.data);
export const apiListDeposits = () => client.get("/wallet/deposit-requests").then((r) => r.data);
export const apiActionDeposit = (id, approve) =>
  client.post(`/admin/deposit-requests/${id}/action?approve=${approve}`).then((r) => r.data);
export const apiListTravellers = () => client.get("/travellers").then((r) => r.data);
export const apiAddTraveller = (p) => client.post("/travellers", p).then((r) => r.data);
export const apiDelTraveller = (id) => client.delete(`/travellers/${id}`).then((r) => r.data);
export const apiListOffers = () => client.get("/offers").then((r) => r.data);
export const apiAddOffer = (p) => client.post("/admin/offers", p).then((r) => r.data);
export const apiDelOffer = (id) => client.delete(`/admin/offers/${id}`).then((r) => r.data);
export const apiReportsSummary = () => client.get("/reports/summary").then((r) => r.data);
export const apiStatement = () => client.get("/statement").then((r) => r.data);
export const apiCreateServiceReq = (p) => client.post("/service-requests", p).then((r) => r.data);
export const apiListServiceReq = () => client.get("/service-requests").then((r) => r.data);
export const apiResolveServiceReq = (id) => client.post(`/service-requests/${id}/resolve`).then((r) => r.data);
export const apiListBanks = () => client.get("/bank-accounts").then((r) => r.data);
export const apiAddBank = (p) => client.post("/admin/bank-accounts", p).then((r) => r.data);
export const apiDelBank = (id) => client.delete(`/admin/bank-accounts/${id}`).then((r) => r.data);
export const apiListAmendments = (type) =>
  client.get("/amendments", { params: type ? { type } : {} }).then((r) => r.data);
export const apiCreateAmendment = (p) => client.post("/amendments", p).then((r) => r.data);
export const apiAmendmentStatus = (id, status) =>
  client.post(`/admin/amendments/${id}/status?status=${status}`).then((r) => r.data);
export const apiGetInvoice = (id) => client.get(`/invoices/${id}`).then((r) => r.data);
export const apiAgentReports = () => client.get("/reports/agent").then((r) => r.data);
export const apiAdminAgentReports = () => client.get("/admin/reports/agents").then((r) => r.data);
export const apiFareCalendar = (frm, to, month) =>
  client.get("/flights/fare-calendar", { params: { frm, to, month } }).then((r) => r.data);

// ---- Holidays ----
export const apiListHolidays = (params = {}) => client.get("/holidays", { params }).then((r) => r.data);
export const apiGetHoliday = (id) => client.get(`/holidays/${id}`).then((r) => r.data);
export const apiHolidayEnquiry = (payload) => client.post("/holidays/enquiry", payload).then((r) => r.data);
export const apiAdminListHolidays = () => client.get("/admin/holidays").then((r) => r.data);
export const apiAdminCreateHoliday = (payload) => client.post("/admin/holidays", payload).then((r) => r.data);
export const apiAdminUpdateHoliday = (id, payload) => client.put(`/admin/holidays/${id}`, payload).then((r) => r.data);
export const apiAdminDeleteHoliday = (id) => client.delete(`/admin/holidays/${id}`).then((r) => r.data);
export const apiAdminHolidayEnquiries = () => client.get("/admin/holiday-enquiries").then((r) => r.data);

// ---- Website Settings: Banners ----
export const apiListBanners = () => client.get("/site/banners").then((r) => r.data);
export const apiAdminListBanners = () => client.get("/admin/banners").then((r) => r.data);
export const apiAdminCreateBanner = (p) => client.post("/admin/banners", p).then((r) => r.data);
export const apiAdminUpdateBanner = (id, p) => client.put(`/admin/banners/${id}`, p).then((r) => r.data);
export const apiAdminDeleteBanner = (id) => client.delete(`/admin/banners/${id}`).then((r) => r.data);

// ---- SEO ----
export const apiGetSeo = (page) => client.get("/site/seo", { params: { page } }).then((r) => r.data);
export const apiAdminListSeo = () => client.get("/admin/seo").then((r) => r.data);
export const apiAdminSetSeo = (p) => client.post("/admin/seo", p).then((r) => r.data);

// ---- Subscribers ----
export const apiSubscribe = (email) => client.post("/subscribers", { email }).then((r) => r.data);
export const apiAdminSubscribers = () => client.get("/admin/subscribers").then((r) => r.data);

// ---- Finance: Notes & Refunds ----
export const apiAdminListNotes = () => client.get("/admin/notes").then((r) => r.data);
export const apiAdminCreateNote = (p) => client.post("/admin/notes", p).then((r) => r.data);
export const apiAdminDeleteNote = (id) => client.delete(`/admin/notes/${id}`).then((r) => r.data);
export const apiCreateRefund = (p) => client.post("/refunds", p).then((r) => r.data);
export const apiListRefunds = () => client.get("/refunds").then((r) => r.data);
export const apiRefundStatus = (id, status) => client.post(`/admin/refunds/${id}/status?status=${status}`).then((r) => r.data);

// ---- Notifications ----
export const apiListNotifications = () => client.get("/notifications").then((r) => r.data);
export const apiReadNotification = (id) => client.post(`/notifications/${id}/read`).then((r) => r.data);
export const apiReadAllNotifications = () => client.post("/notifications/read-all").then((r) => r.data);

// ---- Trip Cart ----
export const apiCartCheckout = (payload) => client.post("/cart/checkout", payload).then((r) => r.data);
export const apiCreateRzpOrder = (payload) => client.post("/payments/create-order", payload).then((r) => r.data);
export const apiVerifyRzp = (payload) => client.post("/payments/verify", payload).then((r) => r.data);
export const apiCreateDirectOrder = (payload) => client.post("/payments/order", payload).then((r) => r.data);
export const apiVerifySignature = (payload) => client.post("/payments/verify-signature", payload).then((r) => r.data);

// ---- Supplier readiness ----
export const apiSupplierReadiness = (type) => client.get(`/supplier/readiness?type=${type}`).then((r) => r.data);
export const apiAdminSupplierStatus = () => client.get("/admin/supplier/status").then((r) => r.data);
export const apiAdminSupplierToggle = (product, off) => client.post(`/admin/supplier/toggle?product=${product}&off=${off}`).then((r) => r.data);
export const apiAdminTripjackBalance = () => client.get("/admin/tripjack-balance").then((r) => r.data);
export const apiAdminTripjackBalanceHistory = () => client.get("/admin/tripjack-balance/history").then((r) => r.data);

// ---- Guest checkout + Email OTP ----
export const apiGuestCheckout = (payload) => client.post("/auth/guest", payload).then((r) => r.data);
export const apiOtpVerify = (payload) => client.post("/auth/otp-verify", payload).then((r) => r.data);
export const apiAutoRefund = (payload) => client.post("/payments/auto-refund", payload).then((r) => r.data);
